import {deserializeMaterialGraph, TextureFromRevision, TextureFromSet, TextureSet, TextureType} from "@cm/material-nodes"
import type {MaterialNode} from "@cm/material-nodes"
import type {ImageMapInfo, InputMapsInfo, LegacyId, NonNullableDeep, RequiredDeep} from "#material-maps-exporter/core/types"
import {materialMapsExporterLogger, PLATFORM_TEXTURE_TYPE_TO_CHANNEL_LAYOUT, CM_TO_INCH} from "#material-maps-exporter/core/types"
import type {MapsExporterDataSource, SlotHeadDataSource, TextureRevisionSnapshot} from "#material-maps-exporter/core/data-source"
import {
    analyzeMaterialGraph,
    bsdfInputLabel,
    incompleteHeadMessage,
    MaterialGraphUnsupportedError,
    noBsdfMessage,
} from "#material-maps-exporter/core/analyze-material-graph"
import type {BsdfInput, MaterialGraphAnalysis, MaterialGraphIssue} from "#material-maps-exporter/core/analyze-material-graph"

// The refusal and its issue shape moved into the analysis module; re-exported so `Gather` keeps naming them.
export {MaterialGraphUnsupportedError} from "#material-maps-exporter/core/analyze-material-graph"
export type {MaterialGraphIssue} from "#material-maps-exporter/core/analyze-material-graph"

type ValidatedTextureRevision = NonNullableDeep<RequiredDeep<TextureRevisionSnapshot>>

type MapInfoData = InputMapsInfo[keyof InputMapsInfo]["data"]
type ExtendedMapInfoData = {data: Extract<MapInfoData, {type: "DataObject"}>; textureType: TextureType}

const refuseIncompleteHead = (node: TextureFromRevision | TextureSet | TextureFromSet) => {
    const incomplete = incompleteHeadMessage(node)
    if (incomplete !== undefined) throw new MaterialGraphUnsupportedError(incomplete, node.nodeClass)
}

const getExtendedMapInfoDataForTextureFromRevision = async (dataSource: SlotHeadDataSource, node: TextureFromRevision): Promise<ExtendedMapInfoData> => {
    refuseIncompleteHead(node)
    const resolvedTextureRevision = await dataSource.resolveTextureRevision(node.parameters.settings.textureRevisionLegacyId!)
    const resolvedImageDataObject = await dataSource.resolveDataObject(resolvedTextureRevision.dataObjectId)

    return {
        data: {
            type: "DataObject",
            dataObjectId: resolvedTextureRevision.dataObjectLegacyId,
            width: resolvedImageDataObject.width!,
            height: resolvedImageDataObject.height!,
            physicalWidth: resolvedTextureRevision.widthCm,
            physicalHeight: resolvedTextureRevision.heightCm,
            displacementScale: resolvedTextureRevision.displacementCm,
        },
        textureType: resolvedTextureRevision.textureType,
    }
}

const getExtendedMapInfoDataForTextureSetReferencingNodeAndTextureType = async (
    dataSource: SlotHeadDataSource,
    node: TextureSet | TextureFromSet,
    textureType: TextureType,
): Promise<ExtendedMapInfoData | undefined> => {
    const textureSetRevisionId = node.parameters.settings.textureSetRevisionId
    if (!textureSetRevisionId) throw new MaterialGraphUnsupportedError(`Missing textureSetRevisionId parameter on ${node.nodeClass} node`, node.nodeClass)
    const resolvedTextureSetRevision = await dataSource.resolveTextureSetRevision(textureSetRevisionId)

    const assignmentForTextureType = resolvedTextureSetRevision.mapAssignments.find((assignment) => assignment.textureType === textureType)
    if (!assignmentForTextureType) return undefined
    const resolvedImageDataObject = await dataSource.resolveDataObject(assignmentForTextureType.id)

    return {
        data: {
            type: "DataObject",
            dataObjectId: assignmentForTextureType.legacyId,
            width: resolvedImageDataObject.width!,
            height: resolvedImageDataObject.height!,
            physicalWidth: resolvedTextureSetRevision.widthCm,
            physicalHeight: resolvedTextureSetRevision.heightCm,
            displacementScale: resolvedTextureSetRevision.displacementCm,
        },
        textureType: textureType,
    }
}

/** Refuses a miss its callee tolerates: this node names the texture type itself, so a set without it is a dangling reference. */
const getExtendedMapInfoDataForTextureFromSetNode = async (dataSource: SlotHeadDataSource, node: TextureFromSet): Promise<ExtendedMapInfoData> => {
    refuseIncompleteHead(node)
    const textureType = node.mapAssignmentTextureType!
    const mapInfo = await getExtendedMapInfoDataForTextureSetReferencingNodeAndTextureType(dataSource, node, textureType)
    if (!mapInfo) {
        throw new MaterialGraphUnsupportedError(
            `${node.definition.ui?.label ?? node.nodeClass}: the selected texture set has no ${textureType} map.`,
            node.nodeClass,
        )
    }
    return mapInfo
}

export type ResolvedSlot = {
    bsdfInput: BsdfInput
    textureType: TextureType
    result:
        | {kind: "unconnected"}
        /** A `TextureSet` is wired in but holds no map of this type — an ordinary material, not a dangling reference. */
        | {kind: "notInSet"}
        | {kind: "extracted"; data: MapInfoData; transformations: MaterialNode[]}
        | {kind: "unsupported"; issue: MaterialGraphIssue}
}

/**
 * Resolves each analysed slot's chain head through the data source. Shape issues pass through from
 * the analysis; a reference the data cannot satisfy becomes a `data` issue. Every slot is attempted,
 * so a caller can report all of them at once.
 */
export async function resolveSlotHeads(analysis: Extract<MaterialGraphAnalysis, {kind: "bsdf"}>, dataSource: SlotHeadDataSource): Promise<ResolvedSlot[]> {
    const slots: ResolvedSlot[] = []
    for (const {bsdfInput, textureType, analysis: slot} of analysis.slots) {
        if (slot.kind !== "chain") {
            slots.push({bsdfInput, textureType, result: slot.kind === "unconnected" ? {kind: "unconnected"} : {kind: "unsupported", issue: slot.issue}})
            continue
        }
        try {
            const {head, transformations} = slot
            const data: MapInfoData | undefined = await (async () => {
                if (head instanceof TextureFromRevision) return (await getExtendedMapInfoDataForTextureFromRevision(dataSource, head)).data
                else if (head instanceof TextureSet)
                    return (await getExtendedMapInfoDataForTextureSetReferencingNodeAndTextureType(dataSource, head, textureType))?.data
                else if (head instanceof TextureFromSet) return (await getExtendedMapInfoDataForTextureFromSetNode(dataSource, head)).data
                else return {type: "ShaderNodeRGB" as const, shaderNodeRGB: head}
            })()
            slots.push({bsdfInput, textureType, result: data === undefined ? {kind: "notInSet"} : {kind: "extracted", data, transformations}})
        } catch (error) {
            if (!(error instanceof MaterialGraphUnsupportedError)) throw error
            slots.push({
                bsdfInput,
                textureType,
                result: {
                    kind: "unsupported",
                    issue: {
                        bsdfInput,
                        bsdfInputLabel: bsdfInputLabel(analysis.bsdf, bsdfInput),
                        nodeClass: error.nodeClass,
                        category: "data",
                        message: error.message,
                    },
                },
            })
        }
    }
    return slots
}

/** The resolved counterpart of `unreadableSlotIssues`: shape issues passed through, plus the data ones. */
export const issuesForResolvedSlots = (slots: ResolvedSlot[]): MaterialGraphIssue[] =>
    slots.flatMap(({result}) => (result.kind === "unsupported" ? [result.issue] : []))

export async function gatherMaterialData(
    dataSource: MapsExporterDataSource,
    materialId: {legacyId: LegacyId} | {id: string},
): Promise<{
    mapsInfo: InputMapsInfo
    materialRevisionDetails: {
        id: string
        legacyId: number
    }
}> {
    const mapsInfo: InputMapsInfo = {}

    const isValidTextureRevisionDetails = (x: TextureRevisionSnapshot): x is ValidatedTextureRevision => {
        return (
            x.texture != null &&
            typeof x.texture.type === "string" &&
            typeof x.width === "number" &&
            typeof x.height === "number" &&
            x.dataObject != null &&
            typeof x.dataObject.legacyId === "number" &&
            typeof x.dataObject.width === "number" &&
            typeof x.dataObject.height === "number"
        )
    }

    const appendMapInfo = (info: {
        type: TextureType
        dataObjectId: number
        width: number
        height: number
        physicalWidth: number
        physicalHeight: number
        displacementScale?: number
        transmissionMin?: number
        transmissionMax?: number
    }) => {
        const textureType = info.type
        if (mapsInfo[textureType] !== undefined) return
        if (PLATFORM_TEXTURE_TYPE_TO_CHANNEL_LAYOUT[textureType] === undefined) return
        mapsInfo[textureType] = {
            data: {
                type: "DataObject",
                dataObjectId: info.dataObjectId,
                width: info.width,
                height: info.height,
                physicalWidth: info.physicalWidth,
                physicalHeight: info.physicalHeight,
                displacementScale: info.displacementScale,
                transmissionMin: info.transmissionMin,
                transmissionMax: info.transmissionMax,
            },
            transformations: [],
        }
    }

    // Phase 0: Fetch all data at once
    const revisionData = await dataSource.fetchMaterialRevision(materialId)
    const materialGraph = deserializeMaterialGraph(revisionData.graphNode)

    //Phase 1.: Try to get as much as possible by traversing the material graph
    {
        const analysis = analyzeMaterialGraph(materialGraph)
        if (analysis.kind === "noBsdf") throw new MaterialGraphUnsupportedError(noBsdfMessage(analysis))

        const slots = await resolveSlotHeads(analysis, dataSource)

        // Every slot is attempted before refusing, so one message names all of them rather than
        // sending the user back for the next one. Before Phase 2, whose backstop would otherwise
        // fill the slot and make the export look complete.
        const graphIssues = issuesForResolvedSlots(slots)
        if (graphIssues.length > 0) {
            const detail = graphIssues.map((issue) => `${issue.bsdfInputLabel} — ${issue.message}`).join("; ")
            throw new MaterialGraphUnsupportedError(`The material graph cannot be exported: ${detail}`, undefined, graphIssues)
        }

        for (const {textureType, result} of slots) {
            if (result.kind === "extracted") mapsInfo[textureType] = {data: result.data, transformations: result.transformations}
        }
    }

    // TODO: move into backend
    //Phase 2.: Fill potential information from untraversed nodes via texture revisions and texture set revision
    //NOTE: We explicitly ignore textures with MASK type in this step. For the exporter, only the MASK textures which contribute to
    //the alpha input on principled shader are currently relevant. We assume that those have been discovered and correctly
    //accounted for in Phase 1. This way we avoid accidentally interpreting a dirt map (also stored on the platform as a MASK texture) as an alpha map.
    {
        const ignoredTextureTypes = [TextureType.Mask]
        const allNodes = materialGraph.nodes
        const textureFromRevisionNodes = allNodes.filter((node) => node instanceof TextureFromRevision)
        const mapInfoForTextureFromRevisionNodes = await Promise.all(
            textureFromRevisionNodes.map(async (node) => getExtendedMapInfoDataForTextureFromRevision(dataSource, node)),
        )
        const mapInfoForTextureSetNodes = (
            await Promise.all(
                allNodes
                    .filter((node) => node instanceof TextureSet)
                    .map(async (node) => {
                        const textureSetRevisionId = node.parameters.settings.textureSetRevisionId
                        if (!textureSetRevisionId)
                            throw new MaterialGraphUnsupportedError(`Missing textureSetRevisionId parameter on TextureSet node`, node.nodeClass)
                        const resolvedTextureSetRevision = await dataSource.resolveTextureSetRevision(textureSetRevisionId)
                        return Promise.all(
                            resolvedTextureSetRevision.mapAssignments.map(async (assignment) => {
                                const partialMapsInfo = await getExtendedMapInfoDataForTextureSetReferencingNodeAndTextureType(
                                    dataSource,
                                    node,
                                    assignment.textureType,
                                )
                                // Cannot miss: the type being looked up is the one this iteration came from.
                                if (!partialMapsInfo)
                                    throw new MaterialGraphUnsupportedError(
                                        `Texture set revision lost its assignment for texture type ${assignment.textureType}`,
                                        node.nodeClass,
                                    )
                                return {
                                    data: {
                                        ...partialMapsInfo.data,
                                        transmissionMax: node.parameters.settings.transmissionMax,
                                        transmissionMin: node.parameters.settings.transmissionMin,
                                    },
                                    textureType: assignment.textureType,
                                }
                            }),
                        )
                    }),
            )
        ).flat()
        const mapInfoForTextureFromSetNodes = await Promise.all(
            allNodes.filter((node) => node instanceof TextureFromSet).map((node) => getExtendedMapInfoDataForTextureFromSetNode(dataSource, node)),
        )
        ;[...mapInfoForTextureFromRevisionNodes, ...mapInfoForTextureSetNodes, ...mapInfoForTextureFromSetNodes]
            .filter((info) => !ignoredTextureTypes.includes(info.textureType))
            .forEach((info) =>
                appendMapInfo({
                    ...info.data,
                    type: info.textureType,
                }),
            )
        //Heuristic to grab the displacement texture from the normal map if it is not explicitly provided in the material revision
        await (async () => {
            if (mapsInfo[TextureType.Displacement] !== undefined) return
            const normalMapTextureFromRevisionNodeIdx = mapInfoForTextureFromRevisionNodes.findIndex((info) => info.textureType === TextureType.Normal)
            if (normalMapTextureFromRevisionNodeIdx === -1) return
            const normalMapTextureFromRevisionNode = textureFromRevisionNodes[normalMapTextureFromRevisionNodeIdx]
            const normalMapTextureRevisionLegacyId = normalMapTextureFromRevisionNode.parameters.settings.textureRevisionLegacyId
            if (!normalMapTextureRevisionLegacyId) return
            const normalToDisplacementData = await dataSource.fetchNormalToDisplacementRevision(normalMapTextureRevisionLegacyId)
            const displacementTextures =
                normalToDisplacementData.texture?.textureSet.textures.filter((texture) => texture.type === TextureType.Displacement) ?? []
            if (displacementTextures.length !== 1) return
            const normalMapInfo = mapInfoForTextureFromRevisionNodes[normalMapTextureFromRevisionNodeIdx].data
            const matchingDisplacementTextureRevision = displacementTextures[0].revisions.find((displacementRevision) => {
                return (
                    displacementRevision.createdByVersion === normalToDisplacementData.createdByVersion &&
                    displacementRevision.width === normalMapInfo.physicalWidth &&
                    displacementRevision.height === normalMapInfo.physicalHeight
                )
            })
            if (matchingDisplacementTextureRevision && isValidTextureRevisionDetails(matchingDisplacementTextureRevision))
                appendMapInfo({
                    type: matchingDisplacementTextureRevision.texture.type as TextureType,
                    dataObjectId: matchingDisplacementTextureRevision.dataObject.legacyId,
                    width: matchingDisplacementTextureRevision.dataObject.width,
                    height: matchingDisplacementTextureRevision.dataObject.height,
                    physicalWidth: matchingDisplacementTextureRevision.width,
                    physicalHeight: matchingDisplacementTextureRevision.height,
                    displacementScale: matchingDisplacementTextureRevision.displacement ?? undefined,
                })
        })()
    }

    return {mapsInfo, materialRevisionDetails: {id: revisionData.id, legacyId: revisionData.legacyId}}
}

export type AveragePhysicalSizeInfo = {
    widthCm: number
    heightCm: number
    inhomogeneousPhysicalSizesDetected: boolean
}

export function getAveragePhysicalSizeInfo(imageMapsInfo: ImageMapInfo[]): AveragePhysicalSizeInfo {
    const retValue = imageMapsInfo.reduce<{widthCm: number; heightCm: number}>(
        (acc, cur) => ({
            widthCm: acc.widthCm + cur.physicalWidth / Object.keys(imageMapsInfo).length,
            heightCm: acc.heightCm + cur.physicalHeight / Object.keys(imageMapsInfo).length,
        }),
        {widthCm: 0, heightCm: 0},
    )

    let inhomogeneousPhysicalSizesDetected = false
    for (const imageMapInfo of imageMapsInfo) {
        if (Math.abs(imageMapInfo.physicalWidth - retValue.widthCm) > 0.1 || Math.abs(imageMapInfo.physicalHeight - retValue.heightCm) > 0.1) {
            materialMapsExporterLogger?.debug(`Warning: Inhomogeneous physical sizes detected for export, DPI information will not be set`)
            inhomogeneousPhysicalSizesDetected = true
        }
    }

    return {...retValue, inhomogeneousPhysicalSizesDetected}
}

export function estimateCommonSizeForImageMaps(
    imageMapsInfo: ImageMapInfo[],
    targetDpi: "original" | number,
    avgPhysicalSizeInfo: AveragePhysicalSizeInfo,
): {
    widthPx: number
    heightPx: number
    dpi?: number
} {
    let majoritySize: [number, number] | undefined = undefined
    let majorityCount = 0
    const sizeCount = new Map<string, number>()

    for (const imageMapInfo of imageMapsInfo) {
        const sizeStr = `${imageMapInfo.width}-${imageMapInfo.height}`
        let count = sizeCount.get(sizeStr)
        count = typeof count === "undefined" ? 1 : count + 1
        sizeCount.set(sizeStr, count)
        if (count > majorityCount) {
            majorityCount = count
            majoritySize = [imageMapInfo.width, imageMapInfo.height]
        }
    }

    const retValue = (() => {
        if (majoritySize) {
            if (avgPhysicalSizeInfo.inhomogeneousPhysicalSizesDetected) return {widthPx: majoritySize[0], heightPx: majoritySize[1], dpi: undefined}

            const currentDpi = (() => {
                const dpiX = majoritySize[0] / (avgPhysicalSizeInfo.widthCm * CM_TO_INCH)
                const dpiY = majoritySize[1] / (avgPhysicalSizeInfo.heightCm * CM_TO_INCH)
                const dpi = (dpiX + dpiY) / 2
                return dpi
            })()

            if (targetDpi === "original") {
                return {
                    widthPx: majoritySize[0],
                    heightPx: majoritySize[1],
                    dpi: currentDpi,
                }
            } else {
                const scale = targetDpi / currentDpi
                return {
                    widthPx: Math.round(majoritySize[0] * scale),
                    heightPx: Math.round(majoritySize[1] * scale),
                    dpi: targetDpi,
                }
            }
        } else if (targetDpi !== "original") throw new Error(`No majority size found, cannot scale to target DPI ${targetDpi}`)
        else
            return {
                widthPx: 256,
                heightPx: 256,
                dpi: undefined,
            }
    })()

    return retValue
}
